import type { ReactNode } from "react";
import React, { useRef } from "react";

import { Box } from "@mui/material";

import { Footer } from "common";
import { makeStyles, useChatbotState } from "hooks";

interface DefaultLayoutProps {
  children: ReactNode;
}

const useStyles = makeStyles((theme) => ({
  container: {
    height: "100vh",
    overflowY: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
  },
  headerWrapper: {
    width: "100%",
    flexShrink: 0,
  },
  contentWrapper: {
    display: "flex",
    width: "100%",
    flex: 1,
    overflow: "hidden",
    minHeight: 0,
  },
  mainContent: {
    flex: 1,
    transition: "width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
    overflow: "hidden",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
}));

const chatStyles = (
  shouldShowChatbotContainer: boolean,
  experiementalChatbotEnabled: boolean,
  shouldShrinkContent: boolean,
) => {
  const footerHeight = 52;

  return {
    chatbotContainer: {
      width: shouldShowChatbotContainer ? "600px" : "0px",
      minWidth: shouldShowChatbotContainer ? "600px" : "0px",
      maxWidth: shouldShowChatbotContainer ? "600px" : "0px",
      visibility: shouldShowChatbotContainer ? "visible" : "hidden",
      position: experiementalChatbotEnabled ? "relative" : "absolute",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      height:
        experiementalChatbotEnabled && shouldShowChatbotContainer
          ? `calc(100% - ${footerHeight}px)`
          : "auto",
      padding: 0,
      margin: 0,
      flexShrink: 0,
      flexGrow: 0,
      flexBasis: shouldShowChatbotContainer ? "600px" : "0px",
      zIndex: 1000,
    },
    mainContent: {
      width: shouldShrinkContent ? "calc(100% - 600px)" : "100%",
    },
    chatbotInnerContainer: {
      width: "100%",
      height: "100%",
    },
  };
};

export function DefaultLayout(props: DefaultLayoutProps) {
  const { children } = props;
  const chatbotContainerRef = useRef<HTMLDivElement>(null);
  const classes = useStyles();
  const { isChatbotOpen, shouldShrinkContent, experiementalChatbotEnabled } =
    useChatbotState();
  const shouldShowChatbotContainer = isChatbotOpen;
  const styles = chatStyles(
    shouldShowChatbotContainer,
    experiementalChatbotEnabled,
    shouldShrinkContent,
  );

  const childrenArray = React.Children.toArray(children);
  const headerElements: ReactNode[] = [];
  const mainContentElements: ReactNode[] = [];

  childrenArray.forEach((child) => {
    if (React.isValidElement(child)) {
      const layoutRole = (child.props as any)?.["data-layout-role"];
      const isHeader = layoutRole === "header";

      if (isHeader) {
        headerElements.push(child);
      } else {
        mainContentElements.push(child);
      }
    } else {
      mainContentElements.push(child);
    }
  });

  return (
    <Box className={classes.container}>
      {headerElements.length > 0 && (
        <Box className={classes.headerWrapper}>{headerElements}</Box>
      )}
      <Box className={classes.contentWrapper}>
        <Box
          component="main"
          className={classes.mainContent}
          sx={styles.mainContent}
        >
          {mainContentElements}
        </Box>
        <Box sx={styles.chatbotContainer}>
          <Box
            id="chatbot-container"
            ref={chatbotContainerRef}
            style={styles.chatbotInnerContainer}
          ></Box>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
}
